import { formatISO } from 'date-fns'
import {
  type CalendarFilterState,
  CalendarFilterCategoryIds,
  type CalendarValueTree,
  type CalendarValueTrees,
  categoryFilterGroups,
} from '@opallabs/filters'

/**
 * Where the in-market preview of a stretch of days lives, with whatever
 * filters the calendar is under carried into it.
 *
 * Two places send a person there and each counts the days its own way: the
 * header previews what the calendar is showing, and a month day previews
 * itself. The route belongs to the asgard router, which the calendar reaches
 * by announcing it to the parent frame rather than by navigating, so the
 * Backbone router — and the view it holds — are left alone.
 */
export const inMarketPreviewRoute = (
  slug: string,
  start: Date,
  end: Date,
  extraParams: Record<string, string> = {}
): string => {
  const params = new URLSearchParams({
    startDate: formatISO(start, { representation: 'date' }),
    endDate: formatISO(end, { representation: 'date' }),
    ...extraParams,
  })

  return `/moments/${slug}/in_market_preview?${params.toString()}`
}

const CONTENT_STATUS_PREFIX = 'content.'

const NO_TREE: CalendarValueTree = {
  childrenOf: new Map(),
  modeScope: 'category',
  parentOf: new Map(),
}

type MatchedValues = Readonly<{
  carved: readonly string[]
  chosen: readonly string[]
  tree: CalendarValueTree
}>

/**
 * What one category of the month matches, and the tree its values sit in.
 * The preview can only narrow to what is matched, so what the month hides is
 * left out.
 */
const matchedIn = (
  filters: CalendarFilterState,
  trees: CalendarValueTrees,
  categoryId: string
): MatchedValues => {
  const filter = filters.cats[categoryId]
  const tree = trees[categoryId]
  if (filter === undefined || tree === undefined)
    return { carved: [], chosen: [], tree: NO_TREE }

  const matching = categoryFilterGroups(filter, tree).find(
    (group) => group.mode === 'include'
  )
  return {
    carved: matching?.carved ?? [],
    chosen: matching?.chosen ?? [],
    tree,
  }
}

/** The rows under a parent that are not carved out, or the value itself. */
const openParent = (
  value: string,
  { carved, tree }: MatchedValues
): readonly string[] =>
  tree.childrenOf.get(value)?.filter((child) => !carved.includes(child)) ?? [
    value,
  ]

/**
 * The month's filters in the words the in-market preview reads: `labels`,
 * `channels`, `accounts` and `statuses`. Filters the preview has no word for,
 * such as stories or moment statuses, are left out.
 */
export const monthFilterPreviewParams = (
  filters: CalendarFilterState,
  trees: CalendarValueTrees
): Record<string, string> => {
  const params: Record<string, string> = {}
  const write = (key: string, values: readonly string[]) => {
    if (values.length > 0) params[key] = values.join(',')
  }

  // A label set and the content status row are not values the preview knows,
  // so each stands for the rows under it.
  const labels = matchedIn(filters, trees, CalendarFilterCategoryIds.label)
  write(
    'labels',
    labels.chosen.flatMap((value) => openParent(value, labels))
  )

  // A whole channel stays a channel. One with accounts carved out of it
  // stands for the accounts that are kept.
  const channels = matchedIn(filters, trees, CalendarFilterCategoryIds.channels)
  const { parentOf } = channels.tree
  const channelValues = channels.chosen.flatMap((value) =>
    channels.carved.some((carved) => parentOf.get(carved) === value)
      ? openParent(value, channels)
      : [value]
  )
  write(
    'channels',
    channelValues.filter((value) => !parentOf.has(value))
  )
  write(
    'accounts',
    channelValues.filter((value) => parentOf.has(value))
  )

  const statuses = matchedIn(filters, trees, CalendarFilterCategoryIds.status)
  write(
    'statuses',
    statuses.chosen
      .flatMap((value) => openParent(value, statuses))
      .filter((value) => value.startsWith(CONTENT_STATUS_PREFIX))
      .map((value) => value.slice(CONTENT_STATUS_PREFIX.length))
  )

  return params
}
